import { memo } from 'react';

import { Badge } from '@components/badge';
import { ListCard } from '@components/list-card';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';

import { JobLabels, MAX_LABELS_ON_LIST } from 'components/job-labels';
import { usePermissions } from 'features/permissions';
import { useManageJobAccess } from 'hooks/use-manage-job-access';
import { useStore } from 'models/Provider';
import type { JobListRow } from 'types/jobs';

import {
  jobDateText,
  jobDetailUrl,
  jobListValues,
  jobMetaLine,
  jobTitle,
  Translate,
} from './job-display';
import { useJobStateIconContainer } from './job-state';
import { jobsLocale } from './jobs-config';
import { useJobActions } from './use-job-actions';

type JobListItemProps = {
  job: JobListRow;
  /** Owned by the list so per-row resize listeners and query observers do not scale with rows. */
  width: number;
  buildActionItems: ReturnType<typeof useJobActions>;
};

export const JobListItem = memo(({ job, width, buildActionItems }: JobListItemProps) => {
  const { t, lang, current } = useStore();
  const translate = t as Translate;
  const { t: tJobs } = useScopedTranslate(jobsLocale);
  const { hasRolePermission } = usePermissions();
  const hasManageAccess = useManageJobAccess(job.createdById);
  const stateIconContainer = useJobStateIconContainer(job);

  const labels = job.labels ?? [];
  const values = jobListValues({ job, width, t: tJobs });
  const defaultLanguage = current?.info?.defaultLanguage;
  const title = jobTitle({ job, defaultLanguage, t: translate });

  const privateBadge =
    job.visibility === 'private' ? (
      <Tooltip content={translate('job.tips.notvisible')}>
        <Badge variant="purple" text={tJobs('privateJob')} />
      </Tooltip>
    ) : undefined;

  return (
    <ListCard
      to={jobDetailUrl(job)}
      spaceX={6}
      spaceY={4}
      iconContainer={stateIconContainer}
      dataCy={title}
      title={title}
      titleFont="bold16"
      description={jobMetaLine({ job, lang, defaultLanguage, t: translate })}
      details={
        <Text
          font="regular14"
          color="textSecondary"
          text={jobDateText({ job, lang, t: translate })}
        />
      }
      actions={<ValueList items={values} />}
      actionItems={
        hasRolePermission('job:options') ? buildActionItems(job, hasManageAccess) : undefined
      }
      // ListCard renders its footer slot for any truthy `footer`, even one that renders null.
      footer={
        labels.length > 0 || privateBadge ? (
          <JobLabels labels={labels} max={MAX_LABELS_ON_LIST} leading={privateBadge} />
        ) : undefined
      }
    />
  );
});
